import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';   // ← ajoute useNavigate
import { useAuth } from '../context/AuthContext';
import './Landing.css';

/* ─── Données ─── */
const FEATURES = [
  { icon: 'fa-sync-alt', title: 'Synchro multi-appareils', desc: 'Vos documents disponibles partout, tout le temps, en temps réel.' },
  { icon: 'fa-paperclip', title: 'Pièces jointes', desc: 'Ajoutez fichiers, liens et notes contextuelles à chaque document.' },
  { icon: 'fa-users', title: 'Collaboration d\'équipe', desc: 'Partagez et co-éditez vos documents avec vos collaborateurs.' },
  { icon: 'fa-robot', title: 'CV optimisé ATS', desc: 'Soyez sélectionné par les algorithmes de recrutement automatiques.' },
  { icon: 'fa-shield-alt', title: 'Conformité légale', desc: 'Factures conformes TVA, numérotation automatique, mentions obligatoires.' },
  { icon: 'fa-file-export', title: 'Export PDF haute qualité', desc: 'Rendu vectoriel parfait, prêt à imprimer ou envoyer par email.' },
];

const TESTIMONIALS = [
  { avatar: 'https://i.pravatar.cc/60?img=32', name: 'Sarah Morel', role: 'Freelance Designer', text: 'Je génère mes factures en moins de 2 minutes. Un gain de temps énorme pour ma comptabilité.' },
  { avatar: 'https://i.pravatar.cc/60?img=45', name: 'Karim Benali', role: 'Développeur Full-Stack', text: 'Mon CV ATS m\'a valu 3 entretiens en une semaine après des mois de silence. Bluffant.' },
  { avatar: 'https://i.pravatar.cc/60?img=13', name: 'Marie Fontaine', role: 'Directrice Marketing, Innov\'', text: 'Nous avons équipé toute notre équipe commerciale. La qualité des documents a transformé notre image.' },
];

const LOGOS = ['Google', 'Microsoft', 'Airbnb', 'Spotify', 'Stripe', 'Notion'];

/* ─── Composant ─── */
const PLANS = [
  {
    name: 'Free', price: '0', period: 'pour toujours',
    desc: 'Pour découvrir Flow Generate sans engagement.',
    cta: 'Commencer gratuitement', ctaStyle: 'ghost',
    features: [
      { ok: true, text: '3 CV par mois' },
      { ok: true, text: '3 factures par mois' },
      { ok: true, text: 'Modèles de base' },
      { ok: true, text: 'Export PDF' },
      { ok: false, text: 'Sans logo Flow Generate' },
      { ok: false, text: 'Modèles premium' },
      { ok: false, text: 'Collaboration équipe' },
      { ok: false, text: 'Support prioritaire' },
    ],
  },
  {
    name: 'Pro', price: '9', period: 'par mois',
    desc: 'Pour les freelances et professionnels actifs.',
    badge: '⚡ Le plus populaire',
    cta: 'Essayer 14 jours gratuits', ctaStyle: 'solid',
    features: [
      { ok: true, text: 'CV & factures illimités' },
      { ok: true, text: 'Tous les modèles premium' },
      { ok: true, text: 'Sans logo Flow Generate' },
      { ok: true, text: 'Export PDF haute qualité' },
      { ok: true, text: 'Historique complet' },
      { ok: true, text: 'Optimisation ATS avancée' },
      { ok: false, text: 'Collaboration équipe' },
      { ok: false, text: 'API & webhooks' },
    ],
  },
  {
    name: 'Business', price: '29', period: 'par mois',
    desc: 'Pour les équipes qui veulent aller plus loin.',
    cta: 'Contacter les ventes', ctaStyle: 'ghost',
    features: [
      { ok: true, text: 'Tout ce qu\'inclut Pro' },
      { ok: true, text: 'Jusqu\'à 10 membres' },
      { ok: true, text: 'Collaboration temps réel' },
      { ok: true, text: 'Tableau de bord équipe' },
      { ok: true, text: 'API & webhooks' },
      { ok: true, text: 'Facturation centralisée' },
      { ok: true, text: 'Onboarding dédié' },
      { ok: true, text: 'Support 24/7 prioritaire' },
    ],
  },
];

const Landing = () => {
  const { user } = useAuth();                            // ← ajoute
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [activeTestim, setActiveTestim] = useState(0);
  const [navScrolled, setNavScrolled] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrollY(window.scrollY);
      setNavScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const t = setInterval(() => setActiveTestim(p => (p + 1) % TESTIMONIALS.length), 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  return (
    <div className="lp-root">

      <nav className={`lp-nav ${navScrolled ? 'lp-nav-scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <div className="lp-logo">
            <div className="lp-logo-icon"><i className="fas fa-file-pdf"></i></div>
            <span>Flow<strong>Generate</strong></span>
          </div>

          <ul className="lp-nav-links">
            <li><a href="#features">Fonctionnalités</a></li>
            <li><a href="#cv">CV</a></li>
            <li><a href="#factures">Factures</a></li>
            <li><a href="#tarifs">Tarifs</a></li>
          </ul>

          <div className="lp-nav-cta">
            <Link to="/login" className="lp-btn-ghost">Connexion</Link>
            <Link to="/login?mode=signup" className="lp-btn-solid">
              S'inscrire <i className="fas fa-arrow-right"></i>
            </Link>
          </div>

          <button className="lp-hamburger" onClick={() => setNavOpen(o => !o)}>
            <i className={`fas ${navOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </button>
        </div>

        {/* Mobile drawer */}
        <div className={`lp-mobile-drawer ${navOpen ? 'lp-drawer-open' : ''}`}>
          <a href="#features" onClick={() => setNavOpen(false)}>Fonctionnalités</a>
          <a href="#cv" onClick={() => setNavOpen(false)}>CV</a>
          <a href="#factures" onClick={() => setNavOpen(false)}>Factures</a>
          <a href="#tarifs" onClick={() => setNavOpen(false)}>Tarifs</a>
          <div className="lp-drawer-cta">
            <Link to="/login" className="lp-btn-ghost" onClick={() => setNavOpen(false)}>Connexion</Link>
            <Link to="/login?mode=signup" className="lp-btn-solid" onClick={() => setNavOpen(false)}>S'inscrire</Link>
          </div>
        </div>
      </nav>

      {/* ══════════════════ HERO ══════════════════ */}
      <section className="lp-hero">
        {/* blobs décoratifs */}
        <div className="lp-blob lp-blob-1"></div>
        <div className="lp-blob lp-blob-2"></div>
        <div className="lp-blob lp-blob-3"></div>

        <div className="lp-hero-inner">
          {/* Colonne gauche */}
          <div className="lp-hero-text">
            <div className="lp-badge">
              <span className="lp-badge-dot"></span>
              10 000+ professionnels nous font confiance
            </div>

            <h1 className="lp-hero-h1">
              Propulsez votre<br />
              <span className="lp-gradient-word">carrière</span> &amp;<br />
              gérez votre <span className="lp-gradient-word">business</span>
            </h1>

            <p className="lp-hero-sub">
              CV optimisés ATS et factures conformes — créés en quelques clics,
              téléchargés en PDF haute qualité.
            </p>

            <div className="lp-hero-actions">
              <Link to="/login?mode=signup" className="lp-cta-primary">
                Créer mon premier document
                <i className="fas fa-arrow-right"></i>
              </Link>
              <a href="#features" className="lp-cta-secondary">
                <span className="lp-play-icon"><i className="fas fa-play"></i></span>
                Voir comment ça marche
              </a>
            </div>

            <div className="lp-social-proof">
              <div className="lp-avatars">
                {[1, 2, 3, 4, 5].map(i => (
                  <img key={i} src={`https://i.pravatar.cc/36?img=${i + 20}`} alt="" className="lp-av" />
                ))}
              </div>
              <div className="lp-proof-text">
                <div className="lp-stars">{'★'.repeat(5)}</div>
                <span>+10 000 utilisateurs actifs</span>
              </div>
              <div className="lp-trustpilot">
                <svg viewBox="0 0 24 24" width="16"><path fill="#00b67a" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                <strong>4.9</strong> <span>Trustpilot</span>
              </div>
            </div>
          </div>

          {/* Colonne droite — phone + cartes */}
          <div className="lp-hero-visual" style={{ '--sy': `${scrollY}px` }}>

            {/* Mockup central */}
            <div className="lp-phone">
              <div className="lp-phone-shell">
                <div className="lp-phone-notch"></div>
                <div className="lp-phone-screen">
                  {/* En-tête app */}
                  <div className="lp-app-header">
                    <div className="lp-app-logo"><i className="fas fa-file-pdf"></i></div>
                    <span>Flow Generate</span>
                    <i className="fas fa-bars lp-app-menu"></i>
                  </div>
                  {/* Salutation */}
                  <div className="lp-app-greet">
                    <div className="lp-app-avatar">KA</div>
                    <div>
                      <p className="lp-app-hello">Bonjour,</p>
                      <h4 className="lp-app-name">Kawsar Ahmed 👋</h4>
                    </div>
                  </div>
                  {/* Card statut */}
                  <div className="lp-app-card">
                    <div className="lp-app-card-header">
                      <svg viewBox="0 0 24 24" width="14"><path fill="#00b67a" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                      <span>Trustpilot</span>
                      <strong>4.8 ★</strong>
                    </div>
                    {[
                      { color: '#6C63FF', icon: 'fa-file-invoice', label: 'Facture Client A', time: '14:30' },
                      { color: '#9B59B6', icon: 'fa-file-alt', label: 'CV Professionnel', time: '15:00' },
                      { color: '#E74C8B', icon: 'fa-download', label: 'PDF Téléchargé', time: '16:00' },
                    ].map((item, i) => (
                      <div key={i} className="lp-task">
                        <div className="lp-task-icon" style={{ background: item.color }}>
                          <i className={`fas ${item.icon}`}></i>
                        </div>
                        <div className="lp-task-info">
                          <p>{item.label}</p>
                          <span>{item.time}</span>
                        </div>
                        <div className="lp-task-check"><i className="fas fa-check"></i></div>
                      </div>
                    ))}
                  </div>
                  {/* Avatars équipe */}
                  <div className="lp-app-team">
                    <div className="lp-app-team-avatars">
                      {[1, 2, 3, 4].map(i => (
                        <img key={i} src={`https://i.pravatar.cc/30?img=${i + 5}`} alt="" />
                      ))}
                      <span>+5</span>
                    </div>
                    <div>
                      <p>Rejoignez la communauté</p>
                      <span>10K+ cette année 🔥</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cartes flottantes */}
            <div className="lp-fcard lp-fc-tl">
              <div className="lp-fc-icon" style={{ background: '#EEF0FF' }}>
                <i className="fas fa-tasks" style={{ color: '#6C63FF' }}></i>
              </div>
              <div>
                <h5>Aperçu du Projet</h5>
                <p>Interface intuitive</p>
              </div>
            </div>

            <div className="lp-fcard lp-fc-tr">
              <div className="lp-fc-stat">
                <strong>29M+</strong>
                <span>Utilisateurs</span>
              </div>
              <div className="lp-fc-stat">
                <strong>100M+</strong>
                <span>Documents</span>
              </div>
            </div>

            <div className="lp-fcard lp-fc-bl">
              <div className="lp-fc-icon" style={{ background: '#FFF0F5' }}>
                <i className="fas fa-palette" style={{ color: '#E74C8B' }}></i>
              </div>
              <div>
                <h5>Personnalisable</h5>
                <p>Votre identité visuelle</p>
              </div>
            </div>

            <div className="lp-fcard lp-fc-br">
              <div className="lp-fc-icon" style={{ background: '#F0FFF4' }}>
                <i className="fas fa-bolt" style={{ color: '#22c55e' }}></i>
              </div>
              <div>
                <h5>Génération rapide</h5>
                <p>En moins de 30 sec</p>
              </div>
            </div>

            {/* Avatars flottants haut droite */}
            <div className="lp-floating-av lp-fav-tr">
              <div className="lp-fav-row">
                {[9, 10, 11, 12].map(i => (
                  <img key={i} src={`https://i.pravatar.cc/32?img=${i}`} alt="" />
                ))}
                <span>+5</span>
              </div>
              <p>Téléchargé par 5M+ 🔥</p>
            </div>
          </div>
        </div>

        {/* Bandeau logos */}
        <div className="lp-logos-band">
          <p>Utilisé par des équipes dans des entreprises comme</p>
          <div className="lp-logos-row">
            {LOGOS.map(l => (
              <div key={l} className="lp-logo-pill">{l}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════ FEATURES ══════════════════ */}
      <section className="lp-features" id="features">
        <div className="lp-section-inner">
          <div className="lp-section-label">FONCTIONNALITÉS</div>
          <h2 className="lp-section-h2">
            Un ensemble complet &amp; puissant<br />
            <span className="lp-gradient-word">pour vos documents</span>
          </h2>
          <p className="lp-section-sub">
            Tout ce dont vous avez besoin pour créer, gérer et partager vos documents professionnels.
          </p>

          <div className="lp-features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="lp-feature-card" style={{ '--delay': `${i * 0.08}s` }}>
                <div className="lp-feature-icon">
                  <i className={`fas ${f.icon}`}></i>
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════ APERÇU DOCS ══════════════════ */}
      <section className="lp-preview" id="cv">
        <div className="lp-section-inner lp-preview-inner">
          <div className="lp-preview-text">
            <div className="lp-section-label">APERÇU</div>
            <h2 className="lp-section-h2">
              Des documents qui<br />
              <span className="lp-gradient-word">impressionnent</span>
            </h2>
            <p>Nos modèles combinent esthétique moderne et optimisation technique — CV lus par les ATS, factures conformes aux normes légales.</p>
            <ul className="lp-preview-list">
              <li><i className="fas fa-check-circle"></i> Éditeur drag &amp; drop intuitif</li>
              <li><i className="fas fa-check-circle"></i> Export PDF vectoriel haute résolution</li>
              <li><i className="fas fa-check-circle"></i> Conformité TVA &amp; mentions légales automatiques</li>
              <li><i className="fas fa-check-circle"></i> Personnalisation couleurs, polices, logo</li>
            </ul>
            <div className="lp-preview-ctas">
              <Link to="/login" className="lp-cta-primary">
                Créer un CV <i className="fas fa-arrow-right"></i>
              </Link>
              <Link to="/login" className="lp-cta-outline" id="factures">
                Créer une Facture
              </Link>
            </div>
          </div>

          {/* Grille de mockup documents */}
          <div className="lp-doc-grid">
            {/* Carte CV */}
            <div className="lp-doc-card lp-doc-cv">
              <div className="lp-doc-label">
                <i className="fas fa-file-alt"></i> CV ATS
              </div>
              <div className="lp-cv-mock">
                <div className="lp-cv-header">
                  <div className="lp-cv-avatar">JD</div>
                  <div>
                    <div className="lp-cv-name">Jean Dupont</div>
                    <div className="lp-cv-role">Développeur Full-Stack</div>
                  </div>
                </div>
                <div className="lp-cv-line l80"></div>
                <div className="lp-cv-line l60"></div>
                <div className="lp-cv-line l90"></div>
                <div className="lp-cv-line l70"></div>
                <div className="lp-cv-section-title">Expériences</div>
                <div className="lp-cv-line l85"></div>
                <div className="lp-cv-line l65"></div>
                <div className="lp-cv-section-title">Compétences</div>
                <div className="lp-cv-tags">
                  {['React', 'Node.js', 'Python', 'AWS'].map(t => (
                    <span key={t} className="lp-cv-tag">{t}</span>
                  ))}
                </div>
              </div>
              <div className="lp-doc-badge lp-badge-ats">✓ Optimisé ATS</div>
            </div>

            {/* Carte Facture */}
            <div className="lp-doc-card lp-doc-invoice">
              <div className="lp-doc-label">
                <i className="fas fa-file-invoice"></i> Facture Pro
              </div>
              <div className="lp-inv-mock">
                <div className="lp-inv-band"></div>
                <div className="lp-inv-body">
                  <div className="lp-inv-row">
                    <span className="lp-inv-key">Facture N°</span>
                    <span className="lp-inv-val">INV-2024-042</span>
                  </div>
                  <div className="lp-inv-row">
                    <span className="lp-inv-key">Client</span>
                    <span className="lp-inv-val">TechCorp SARL</span>
                  </div>
                  <div className="lp-inv-table-head">
                    <span>Description</span><span>Total</span>
                  </div>
                  <div className="lp-inv-item">
                    <span>Développement Web</span><span>2 400 €</span>
                  </div>
                  <div className="lp-inv-item">
                    <span>Conseil &amp; Stratégie</span><span>800 €</span>
                  </div>
                  <div className="lp-inv-total">
                    <span>TOTAL TTC</span><strong>3 840 €</strong>
                  </div>
                </div>
              </div>
              <div className="lp-doc-badge lp-badge-legal">✓ Conforme TVA</div>
            </div>

            {/* Stat flottante */}
            <div className="lp-doc-stat">
              <i className="fas fa-clock"></i>
              <div>
                <strong>~45 sec</strong>
                <span>Temps de création moyen</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════ CTA BAND ══════════════════ */}
      <section className="lp-cta-band">
        <div className="lp-cta-blob1"></div>
        <div className="lp-cta-blob2"></div>
        <div className="lp-section-inner lp-cta-inner">
          <div className="lp-cta-text">
            <h2>Prêt ? Commencez avec <span className="lp-white-word">Flow Generate</span> et vivez une expérience exceptionnelle</h2>
            <p>Simplifiez la création de vos documents, augmentez votre productivité. Rejoignez des milliers d'utilisateurs satisfaits dès aujourd'hui — c'est gratuit pour commencer.</p>
            <Link to="/login" className="lp-cta-white">
              Créer mon premier document gratuitement
              <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
          <div className="lp-cta-devices">
            {/* Laptop */}
            <div className="lp-laptop">
              <div className="lp-laptop-screen">
                <div className="lp-laptop-bar">
                  <div className="lp-laptop-dots"><span></span><span></span><span></span></div>
                  <div className="lp-laptop-url">flowgenerate.app</div>
                </div>
                <div className="lp-laptop-content">
                  <div className="lp-laptop-sidebar">
                    {['fa-home', 'fa-file-invoice', 'fa-file-alt', 'fa-cog'].map((ic, i) => (
                      <div key={i} className={`lp-lap-nav${i === 1 ? ' lp-lap-nav-active' : ''}`}>
                        <i className={`fas ${ic}`}></i>
                      </div>
                    ))}
                  </div>
                  <div className="lp-laptop-main">
                    <div className="lp-lap-header">
                      <div className="lp-lap-title">Générateur de CV</div>
                      <div className="lp-lap-btn"><i className="fas fa-download"></i></div>
                    </div>
                    <div className="lp-lap-card">
                      <div className="lp-lap-line lp-lw70"></div>
                      <div className="lp-lap-line lp-lw90"></div>
                      <div className="lp-lap-line lp-lw55"></div>
                      <div className="lp-lap-divider"></div>
                      <div className="lp-lap-line lp-lw80"></div>
                      <div className="lp-lap-line lp-lw65"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lp-laptop-base"><div className="lp-laptop-foot"></div></div>
            </div>
            {/* Mobile */}
            <div className="lp-cta-mobile">
              <div className="lp-cta-mobile-shell">
                <div className="lp-cta-mobile-notch"></div>
                <div className="lp-cta-mobile-screen">
                  <div className="lp-cm-header">
                    <i className="fas fa-file-pdf"></i><span>Flow Generate</span>
                  </div>
                  <div className="lp-cm-card">
                    <div className="lp-cm-icon"><i className="fas fa-file-alt"></i></div>
                    <div className="lp-cm-info">
                      <div className="lp-cm-line lp-lw80"></div>
                      <div className="lp-cm-line lp-lw55"></div>
                    </div>
                  </div>
                  <div className="lp-cm-card">
                    <div className="lp-cm-icon lp-cm-icon2"><i className="fas fa-file-invoice"></i></div>
                    <div className="lp-cm-info">
                      <div className="lp-cm-line lp-lw70"></div>
                      <div className="lp-cm-line lp-lw45"></div>
                    </div>
                  </div>
                  <div className="lp-cm-dl"><i className="fas fa-download"></i> Télécharger</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════ TARIFS ══════════════════ */}
      <section className="lp-pricing" id="tarifs">
        <div className="lp-section-inner">
          <div className="lp-section-label">TARIFS</div>
          <h2 className="lp-section-h2">Simple, <span className="lp-gradient-word">transparent</span>, sans surprise</h2>
          <p className="lp-section-sub">Commencez gratuitement. Upgradez quand vous en avez besoin. Aucune carte bancaire requise.</p>

          <div className="lp-plans">
            {PLANS.map((plan, i) => (
              <div key={i} className={`lp-plan ${plan.badge ? 'lp-plan-featured' : ''}`}>
                {plan.badge && <div className="lp-plan-badge">{plan.badge}</div>}
                <div className="lp-plan-header">
                  <h3 className="lp-plan-name">{plan.name}</h3>
                  <p className="lp-plan-desc">{plan.desc}</p>
                  <div className="lp-plan-price">
                    <span className="lp-price-currency">€</span>
                    <span className="lp-price-amount">{plan.price}</span>
                    <span className="lp-price-period">/{plan.period}</span>
                  </div>
                </div>
                <ul className="lp-plan-features">
                  {plan.features.map((f, j) => (
                    <li key={j} className={f.ok ? 'lp-feat-ok' : 'lp-feat-no'}>
                      <i className={`fas ${f.ok ? 'fa-check' : 'fa-times'}`}></i>
                      <span>{f.text}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.name === 'Business' ? '/login' : '/login?mode=signup'}
                  className={`lp-plan-cta ${plan.badge ? 'lp-plan-cta-solid' : 'lp-plan-cta-ghost'}`}
                >
                  {plan.cta} <i className="fas fa-arrow-right"></i>
                </Link>
              </div>
            ))}
          </div>

          <p className="lp-pricing-note">
            <i className="fas fa-lock"></i> Paiement sécurisé · Annulation à tout moment · Factures disponibles
          </p>
        </div>
      </section>

      {/* ══════════════════ TESTIMONIALS ══════════════════ */}
      <section className="lp-testimonials">
        <div className="lp-section-inner">
          <div className="lp-section-label">TÉMOIGNAGES</div>
          <h2 className="lp-section-h2">Ce que disent nos <span className="lp-gradient-word">utilisateurs</span></h2>

          <div className="lp-testim-cards">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className={`lp-testim-card ${i === activeTestim ? 'lp-testim-active' : ''}`}>
                <div className="lp-testim-quote"><i className="fas fa-quote-right"></i></div>
                <p className="lp-testim-text">"{t.text}"</p>
                <div className="lp-testim-author">
                  <img src={t.avatar} alt={t.name} />
                  <div>
                    <h5>{t.name}</h5>
                    <span>{t.role}</span>
                  </div>
                  <div className="lp-testim-stars">{'★'.repeat(5)}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="lp-testim-dots">
            {TESTIMONIALS.map((_, i) => (
              <button key={i} className={`lp-dot ${i === activeTestim ? 'lp-dot-active' : ''}`} onClick={() => setActiveTestim(i)}></button>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════ FOOTER ══════════════════ */}
      <footer className="lp-footer">
        <div className="lp-section-inner">
          <div className="lp-footer-top">
            <div className="lp-footer-brand">
              <div className="lp-logo">
                <div className="lp-logo-icon"><i className="fas fa-file-pdf"></i></div>
                <span>Flow<strong>Generate</strong></span>
              </div>
              <p>Simplifiez votre flux de travail et augmentez votre productivité avec des documents professionnels.</p>
              <div className="lp-footer-social">
                {['facebook-f', 'twitter', 'instagram', 'linkedin-in'].map(s => (
                  <a key={s} href="#"><i className={`fab fa-${s}`}></i></a>
                ))}
              </div>
            </div>

            <div className="lp-footer-links-group">
              <h5>Produit</h5>
              <a href="#cv">Générateur de CV</a>
              <a href="#factures">Factures</a>
              <a href="#features">Fonctionnalités</a>
              <a href="#tarifs">Tarifs</a>
            </div>
            <div className="lp-footer-links-group">
              <h5>Ressources</h5>
              <a href="#">Documentation</a>
              <a href="#">Blog</a>
              <a href="#">Support</a>
              <a href="#">API</a>
            </div>
            <div className="lp-footer-links-group">
              <h5>Légal</h5>
              <a href="#">Confidentialité</a>
              <a href="#">CGU</a>
              <a href="#">Cookies</a>
            </div>
          </div>

          <div className="lp-footer-bottom">
            <span>© 2024 FlowGenerate. Tous droits réservés.</span>
            <div className="lp-footer-trust">
              <svg viewBox="0 0 24 24" width="14"><path fill="#00b67a" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
              <span>4.9 Trustpilot</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;